import { getCookie } from './tools.js';

// Base URL for all API calls
const BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function sanitizeEmail(email) {
  return email.replace(/@/g, '_at_').replace(/\./g, '_dot_');
}

/**
 * Constructs headers for API requests.
 * Adds 'Content-Type: application/json' for non-GET requests.
 * Adds 'Authorization: Bearer <token>' if JWT cookie is present.
 * 
 * @param {string} method HTTP method (GET, POST, etc.)
 * @returns {Object} Headers object to include in fetch request
 */
function constructRequestHeaders(method) {
  const headers = {};

  // For all requests except GET, set Content-Type header
  if (method.toLowerCase() !== 'get') {
    headers['Content-Type'] = 'application/json';
  }

  // Retrieve JWT token from cookies
  const jwtToken = getCookie('jwt');
  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  return headers;
}

/**
 * Generic wrapper function to send API requests using fetch.
 * Handles JSON stringification, headers, error handling, and response parsing.
 * 
 * @param {string} method HTTP method ('GET', 'POST', etc.)
 * @param {string} url Relative URL path for the API endpoint
 * @param {Object|string|null} body Request payload for methods like POST, PUT, DELETE
 * @returns {Promise<any>} Resolves to parsed JSON or text response from the server
 * @throws Throws an Error if the request fails or server responds with an error status
 */
export async function sendApiRequest(method, url, body = null) {
  const options = {
    method,
    headers: constructRequestHeaders(method),
    credentials: 'include',
  };

  if (body instanceof FormData) {
    delete options.headers['Content-Type'];
    options.body = body;
  } else if (body) {
    options.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  try {
    const response = await fetch(BASE_URL + url, options);
    const contentType = response.headers.get('content-type');

    if (!response.ok) {
      const errorText = contentType && contentType.includes('application/json')
        ? JSON.stringify(await response.json())
        : await response.text();
      throw new Error(errorText || 'Unknown error');
    }

    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      return await response.text();
    }
  } catch (error) {
    throw error;
  }
}

export async function registerUser(userData) {
  return sendApiRequest('POST', '/auth/register', userData);
}

export async function loginUser(credentials) {
  return sendApiRequest('POST', '/auth/login', credentials);
}

export async function loginAdmin(credentials) {
  return sendApiRequest('POST', '/auth/login/admin', credentials);
}

export async function logoutUser() {
  return sendApiRequest('POST', '/auth/logout');
}